import currentCustomerStore from '@/stores/currentCustomerStore';
import { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import * as AuthApi from '@/api/auth';
import { trackCustomEvent } from './analytics';
import { convertCheckoutTypeToMenuType, getAllUserInfo } from '.';
import usePastaNowBasketStore from '@/stores/pastaNowBasketStore';
import useBasketStore from '@/stores/basketStore';
import { startTokenAuth, completeTokenAuth, setTokenAuthPromise } from './tokenAuthState';

const isLoggedIn = async (): Promise<boolean> => {
  const currentUserStore = currentCustomerStore();
  await currentUserStore.getUserDetails();

  if (currentUserStore.email) {
    getAllUserInfo();
  }

  return Boolean(currentUserStore.email);
};

export const authRouteGuard = async (to: RouteLocationNormalized, next: NavigationGuardNext) => {
  let logged = false;

  if (to.query.token && typeof to.query.token === 'string') {
    startTokenAuth();

    const authPromise = (async () => {
      try {
        await AuthApi.logout();
        await AuthApi.loginWithToken(to.query.token as string);
      } finally {
        completeTokenAuth();
      }
    })();

    setTokenAuthPromise(authPromise);
    await authPromise;
  }

  logged = await isLoggedIn();

  const nonLoggedRoutes = ['login', 'signup', 'password-recover', 'social-login'];
  if (to.name && typeof to.name === 'string' && nonLoggedRoutes.includes(to.name)) {
    logged ? next('/') : next();
  } else {
    // check to see if there is a token for login

    logged ? next() : next({ path: '/social-login', query: { returnTo: to.fullPath } });
  }
};

export const myBasketRouteGuard = async (
  to: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  let logged = false;

  if (to.query.token && typeof to.query.token === 'string') {
    startTokenAuth();

    const authPromise = (async () => {
      try {
        await AuthApi.logout();
        await AuthApi.loginWithToken(to.query.token as string);
      } finally {
        completeTokenAuth();
      }
    })();

    setTokenAuthPromise(authPromise);
    await authPromise;
  }

  const isRK = to.name === 'my-rk-basket';

  logged = await isLoggedIn();
  logged
    ? isRK
      ? next({ name: 'product-box', query: { vc: to.query?.vc } })
      : next({ name: 'pasta-now', query: { vc: to.query?.vc } })
    : next({ name: 'pasta-now-search-address', query: { vc: to.query?.vc } });
};

type CheckoutType = 'one-off' | 'subscription' | 'pasta-now' | 'catering';

const goToStartOfCheckoutOrContinue = (
  to: RouteLocationNormalized,
  next: NavigationGuardNext,
  checkoutType: CheckoutType = 'subscription'
) => {
  const pastaNowBasket = usePastaNowBasketStore();
  const customerStore = currentCustomerStore();
  const basketStore = useBasketStore();

  const redirect = {
    instructions: {
      subscription: 'delivery-instructions',
      'one-off': 'one-off-delivery-instructions',
      'pasta-now': 'pasta-now-delivery-instructions',
      catering: 'catering-delivery-instructions',
    },
    address: {
      subscription: 'delivery-address',
      'one-off': 'one-off-delivery-address',
      'pasta-now': 'pasta-now-delivery-address',
      catering: 'catering-delivery-address',
    },
  };

  if (
    to.path !== '/checkout' &&
    to.path !== '/one-off/checkout' &&
    to.path !== '/pasta-now-checkout' &&
    to.path !== '/catering-checkout'
  ) {
    next();
    return;
  }

  if (checkoutType === 'pasta-now' && !pastaNowBasket.isDelivery)
    next({ name: redirect.instructions[checkoutType], query: { ...to.query } });

  // when trying to access the checkout page,
  // check if we have the delivery address to
  // continue to the instructions screen
  const hasAddress =
    ((checkoutType === 'pasta-now' || checkoutType === 'catering') &&
      pastaNowBasket.address?.address1) ||
    (checkoutType !== 'pasta-now' && checkoutType !== 'catering' && basketStore.address?.address1);
  if (hasAddress && customerStore.firstName && customerStore.lastName) {
    next({ name: redirect.instructions[checkoutType], query: { ...to.query } });
  } else {
    next({ name: redirect.address[checkoutType], query: { ...to.query } });
  }
};

export const checkoutAuthRouteGuard = async (
  to: RouteLocationNormalized,
  next: NavigationGuardNext,
  checkoutType: CheckoutType = 'subscription'
) => {
  const logged = await isLoggedIn();
  const authNames = [
    'checkout-login',
    'checkout-signup',
    'checkout-social-login',
    'pasta-now-checkout-login',
    'pasta-now-checkout-signup',
    'pasta-now-checkout-social-login',
    'catering-checkout-social-login',
    'catering-checkout-signup',
    'catering-checkout-login',
  ];
  // remove duplicated events
  if (logged && to?.path.split('/').length > (checkoutType === 'one-off' ? 3 : 2))
    trackCustomEvent('login_step_in_checkout', {
      login_attempted: 'Bypassed',
      method: 'Bypassed',
      menu_type: convertCheckoutTypeToMenuType(checkoutType),
    });

  if (checkoutType === 'one-off') {
    logged
      ? goToStartOfCheckoutOrContinue(to, next, checkoutType)
      : next({ name: 'social-login', query: { returnTo: 'one-off/menu' } });
    return;
  }

  if (to.name && typeof to.name === 'string' && authNames.includes(to.name)) {
    logged ? goToStartOfCheckoutOrContinue(to, next, checkoutType) : next();
  } else {
    const loginPageName =
      checkoutType === 'subscription'
        ? 'checkout-social-login'
        : checkoutType === 'pasta-now'
          ? 'pasta-now-checkout-social-login'
          : 'catering-checkout-social-login';
    logged ? goToStartOfCheckoutOrContinue(to, next, checkoutType) : next({ name: loginPageName });
  }
};

export const giftAuthRouteGuard = async (
  to: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const logged = await isLoggedIn();
  if (logged)
    trackCustomEvent('login_step_in_checkout', {
      login_attempted: 'Bypassed',
      method: undefined,
      menu_type: 'One-off',
      fulfilment_type: 'delivery',
    });
  logged ? next({ name: 'one-off-delivery-instructions', query: to.query }) : next();
};

export const eatInCheckoutAuthGuard = async (
  to: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const authNames = [
    'eat-in-checkout-login',
    'eat-in-checkout-signup',
    'eat-in-checkout-social-login',
  ];

  const logged = await isLoggedIn();
  const pastaNowBasket = usePastaNowBasketStore();

  if (to.name && typeof to.name === 'string' && authNames.includes(to.name)) {
    if (logged) {
      next({ name: 'eat-in-payment', query: to.query });
    } else next();
  } else {
    if (logged || pastaNowBasket.isGuest) {
      next();
    } else next({ name: 'eat-in-checkout-social-login', query: to.query });
  }
};

export const eventsCheckoutAuthGuard = async (
  to: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const authNames = [
    'events-checkout-login',
    'events-checkout-signup',
    'events-checkout-social-login',
  ];

  const logged = await isLoggedIn();

  if (to.name && typeof to.name === 'string' && authNames.includes(to.name)) {
    if (logged) {
      next({ name: 'events-addons', query: to.query });
    } else next();
  } else {
    if (logged) {
      next();
    } else next({ name: 'events-checkout-social-login', query: to.query });
  }
};

export const orderConfirmationGuard = async (
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const logged = await isLoggedIn();
  if (
    from.name !== 'checkout-payment' &&
    from.name !== 'one-off-checkout-payment' &&
    from.name !== 'paypal-confirmation' &&
    from.name !== 'paypal-setup-confirmation'
  ) {
    logged ? next('/account') : next('/');
  } else {
    next();
  }
};

export const pastaNowNameGuard = async (next: NavigationGuardNext, isCatering?: boolean) => {
  await isLoggedIn();

  const customerStore = currentCustomerStore();
  const pastaNowBasket = usePastaNowBasketStore();

  const hasFirstName = Boolean(pastaNowBasket.customerName?.firstName || customerStore.firstName);
  const hasLastName = Boolean(pastaNowBasket.customerName?.lastName || customerStore.lastName);

  if (!pastaNowBasket.isDelivery) return next();

  if (!hasFirstName || !hasLastName || pastaNowBasket.address?.address1 === 'ignore_me')
    isCatering
      ? next({ name: 'catering-delivery-address', params: { isOpen: 'true' } })
      : next({ name: 'pasta-now-delivery-address', params: { isOpen: 'true' } });
  else next();
};
